"use client";

import { useState, useEffect } from "react";
import { Building, Mail, Globe, Briefcase, Save, Info } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Modal from "../layout/Modal";
import BackButton from "../shared/backButton";
import CustomTooltip from "../shared/CustomTooltip";
import "./OrganizationCreatePage.css";
import {
  getOnboardedOrganizationByIdApi,
  editOnboardedOrganizationApi,
} from "../../services/settingsService";
import { handleApiError as baseHandleApiError } from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import DatePicker from "react-date-picker";
import {
  getOnboardedOrganizationsApi,
  getIndustriesListApi,
} from "../../services/settingsService";
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
const OrganizationEditPage = () => {
  // type ValuePiece = Date | null;
  // type Value = ValuePiece | [ValuePiece, ValuePiece];
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    organizationName: "",
    adminName: "",
    email: "",
    website: "",
    industry: "",
    dueDate: "",
    selectTrial: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
  });
  useEffect(() => {
    // loadOrganization()
    getOnboardedOrganizationByIdApiHandler();
    getIndustriesListApiHandler();
  }, []);

  const [industries, setIndustries] = useState([]);
  const getIndustriesListApiHandler = async () => {
    try {
      const response = await getIndustriesListApi();
      setIndustries(response.data);
    } catch (error) {
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  // const [value, onChange] = useState(setFormData.dueDate || new Date());

  // const getOnboardedOrganizationByIdApiHandler = async () => {
  //   setIsLoading(true);
  //   try {
  //     const response = await getOnboardedOrganizationByIdApi(id);
  //     if (response.code === 200) {
  //       // setOrganization(response.data)
  //       setFormData({
  //         organizationName: response.data.clientName || "",
  //         email: response.data.clientEmail || "",
  //         website: response.data.clientWebsite || "",
  //         industry: response.data.clientIndustry || "",
  //         // format the date to YYYY-MM-DD
  //         dueDate: response.data.clientDueDate
  //           ? new Date(response.data.clientDueDate)
  //           : new Date(),
  //       });
  //       // set due date to the date picker value
  //       // onChange(response.data.clientDueDate || new Date());
  //     }
  //   } catch (error) {
  //     console.error("Error loading organization:", error);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const getOnboardedOrganizationByIdApiHandler = async () => {
    setIsLoading(true);
    try {
      const response = await getOnboardedOrganizationByIdApi(id);
      if (response.code === 200) {
        // Format date to YYYY-MM-DD if present, else use today in same format
        let formattedDueDate = "";
        if (response.data.clientDueDate) {
          const dateObj = new Date(response.data.clientDueDate);
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, "0");
          const day = String(dateObj.getDate()).padStart(2, "0");
          formattedDueDate = `${year}-${month}-${day}`;
        } else {
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, "0");
          const day = String(today.getDate()).padStart(2, "0");
          formattedDueDate = `${year}-${month}-${day}`;
        }

        setFormData({
          organizationName: response.data.clientOrganization || "",
          adminName: response.data.clientName || "",
          email: response.data.clientEmail || "",
          website: response.data.clientWebsite || "",
          industry: response.data.clientIndustry || "",
          dueDate: formattedDueDate,
          selectTrial: response.data.clientStatus || "",
        });
      }
    } catch (error) {
      console.error("Error loading organization:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Mandatory fields
    if (!formData.organizationName.trim()) {
      newErrors.organizationName = t(
        "organizationEdit.errors.organizationNameRequired",
      );
    }

    if (!formData.email.trim()) {
      newErrors.email = t("organizationEdit.errors.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("organizationEdit.errors.emailInvalid");
    }

    if (!formData.website.trim()) {
      newErrors.website = t("organizationEdit.errors.websiteRequired");
    } else if (!/^https?:\/\/.+\..+/.test(formData.website)) {
      newErrors.website = t("organizationEdit.errors.websiteInvalid");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form Data:", formData);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      let obj = {
        id: id,
        organizationName: formData.organizationName,
        industry: formData.industry,
        dueDate: formData.dueDate,
        status: formData.selectTrial,
      };
      console.log("Submitting:", obj);
      let response = await editOnboardedOrganizationApi(obj);
      // Navigate back to organizations list
      if (response.code == 200) {
        setShowModal({
          show: true,
          type: "success",
          title: t("common.success"),
          message:
            response.message || t("organizationEdit.brandUpdatedSuccessfully"),
        });
        setTimeout(() => {
          navigate("/brand");
        }, 1500);
      }
    } catch (error) {
      console.error("Error updating organization:", error);
      handleApiError(error, {
        showModal: true,
        type: "error",
        title: t("common.error"),
        message:
          error.message || t("organizationEdit.failedToUpdateOrganization"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/brand");
  };

  if (isLoading) {
    return (
      <div className="org-create-page">
        <main className="org-create-main">
          <div className="container">
            <div className="org-loading">{t("common.loading")}</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="org-create-page">
      <main className="org-create-main">
        <div className="container">
          <div className="org-create-header">
            <BackButton />

            <div className="org-create-title">
              <div className="org-create-icon">
                <Building size={32} />
              </div>
              <h1>{t("organizationEdit.pageTitle")}</h1>
              <p>{t("organizationEdit.pageDescription")}</p>
            </div>
          </div>

          <div className="org-create-form-container">
            <form onSubmit={handleSubmit} className="org-create-form">
              <div className="org-form-section">
                <h2>{t("organizationEdit.organizationInformation")}</h2>
                <p className="org-section-description">
                  {t("organizationEdit.updateDetails")}
                </p>

                <div className="org-form-grid">
                  <div className="org-form-group ">
                    <div className="org-brand-label-row">
                      <label
                        htmlFor="organizationName"
                        className="org-form-label org-required"
                      >
                        <Building size={16} />
                        {t("organizationDetail.BrandName")}
                      </label>
                      <CustomTooltip
                        tooltip={{
                          message: t(
                            "organizationEdit.brandNameModificationLockedTooltip",
                          ),
                        }}
                        id="organization-edit-name-lock-tooltip"
                        className="org-brand-name-tooltip"
                      >
                        <Info
                          size={14}
                          color="#888"
                          style={{
                            cursor: "pointer",
                            verticalAlign: "middle",
                          }}
                        />
                      </CustomTooltip>
                    </div>
                    <input
                      disabled
                      type="text"
                      id="organizationName"
                      name="organizationName"
                      value={formData.organizationName}
                      onChange={handleInputChange}
                      className={`org-form-input ${
                        errors.organizationName ? "org-error" : ""
                      }`}
                      placeholder={t("organizationEdit.enterOrganizationName")}
                    />
                    {errors.organizationName && (
                      <span className="org-error-message">
                        {errors.organizationName}
                      </span>
                    )}
                  </div>

                  <div className="org-form-group">
                    <div className="org-brand-label-row">
                      <label
                        htmlFor="organizationName"
                        className="org-form-label org-required"
                      >
                        <Building size={16} />
                        {t("organizationDetail.AdminName")}
                      </label>
                      <CustomTooltip
                        tooltip={{
                          message: t(
                            "organizationEdit.adminNameModificationLockedTooltip",
                          ),
                        }}
                        id="organization-edit-name-lock-tooltip"
                        className="org-brand-name-tooltip"
                      >
                        <Info
                          size={14}
                          color="#888"
                          style={{
                            cursor: "pointer",
                            verticalAlign: "middle",
                          }}
                        />
                      </CustomTooltip>
                    </div>
                    <input
                      disabled
                      type="text"
                      id="adminName"
                      name="adminName"
                      value={formData.adminName}
                      onChange={handleInputChange}
                      className={`org-form-input ${
                        errors.adminName ? "org-error" : ""
                      }`}
                      placeholder={t("organizationEdit.enterAdminName")}
                    />
                    {errors.adminName && (
                      <span className="org-error-message">
                        {errors.adminName}
                      </span>
                    )}
                  </div>

                  {/* <div className="org-form-group">
                    <label
                      htmlFor="email"
                      className="org-form-label org-required"
                    >
                      <Mail size={16} />
                      {t("organizationEdit.emailAddress")}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`org-form-input ${
                        errors.email ? "org-error" : ""
                      }`}
                      placeholder={t("organizationEdit.emailPlaceholder")}
                    />
                    {errors.email && (
                      <span className="org-error-message">{errors.email}</span>
                    )}
                  </div> */}
                  {/* 
                  <div className="org-form-group">
                    <label
                      htmlFor="website"
                      className="org-form-label org-required"
                    >
                      <Globe size={16} />
                      {t("organizationInfo.webAddress")}
                    </label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className={`org-form-input ${
                        errors.website ? "org-error" : ""
                      }`}
                      placeholder={t("organizationEdit.websitePlaceholder")}
                    />
                    {errors.website && (
                      <span className="org-error-message">
                        {errors.website}
                      </span>
                    )}
                  </div> */}

                  <div className="org-form-group">
                    <label
                      htmlFor="industry"
                      className="org-form-label org-optional"
                    >
                      <Briefcase size={16} />
                      {t("organizationInfo.industry")}
                      {/* <span className="org-optional-text">
                        ({t("common.optional")})
                      </span> */}
                    </label>
                    <select
                      id="industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleInputChange}
                      className={`org-form-input ${
                        errors.industry ? "org-error" : ""
                      }`}
                    >
                      <option value="">
                        {t("organizationEdit.selectIndustry")}
                      </option>
                      {industries.slice(1).map((industry) => (
                        <option key={industry} value={industry}>
                          {industry}
                        </option>
                      ))}
                    </select>
                    {errors.industry && (
                      <span className="org-error-message">
                        {errors.industry}
                      </span>
                    )}
                  </div>
                  <div className="org-form-group">
                    <label
                      htmlFor="selectTrial"
                      className="org-form-label org-optional"
                    >
                      <Briefcase size={16} />
                      {t("organizationEdit.selectTrial")}
                    </label>
                    <select
                      id="selectTrial"
                      name="selectTrial"
                      value={formData.selectTrial}
                      onChange={handleInputChange}
                      className="org-form-input"
                    >
                      <option value="">{t("organizationEdit.select")}</option>
                      <option value="Trial">
                        {t("organizationEdit.trial")}
                      </option>
                      <option value="Customer">
                        {t("organizationEdit.customer")}
                      </option>
                    </select>
                  </div>

                  {/* DUE DATE PICKER */}
                  <div className="org-form-group org-full-width">
                    <label
                      htmlFor="industry"
                      className="org-form-label org-optional"
                    >
                      <Briefcase size={16} />
                      {t("organizationInfo.DueDate")}
                      {/* <span className="org-optional-text">
                        ({t("common.optional")})
                      </span> */}
                    </label>
                    {/* <DatePicker
                      name="dueDate"
                      // onchange set date to the formdata in YYYY-MM-DD format
                      onChange={(date) =>
                        setFormData({ ...formData, dueDate: date })
                      }
                      value={formData.dueDate}
                    /> */}

                    <DatePicker
                      name="dueDate"
                      onChange={(date) => {
                        if (!date) {
                          setFormData({ ...formData, dueDate: "" });
                          return;
                        }
                        // Format as YYYY-MM-DD in local timezone
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(
                          2,
                          "0",
                        ); // Month is 0-indexed
                        const day = String(date.getDate()).padStart(2, "0");
                        const formattedDate = `${year}-${month}-${day}`;
                        setFormData({ ...formData, dueDate: formattedDate });
                        console.log("Selected date:", formattedDate);
                      }}
                      value={formData.dueDate}
                    />
                  </div>
                </div>
              </div>

              <div className="org-form-actions">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="org-btn org-btn-outline"
                  disabled={isSubmitting}
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  className="org-btn org-btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="org-spinner" />
                      {t("organizationEdit.updating")}
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      {t("organizationEdit.updateOrganization")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      <Modal
        show={showModal.show}
        onClose={() =>
          setShowModal({
            show: false,
            type: "",
            title: "",
            message: "",
            organizationId: null,
          })
        }
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
        showButton={false}
      ></Modal>

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default OrganizationEditPage;
